import type { Role } from "@/shared/schema/user.ts";
import type { BootstrapPayload } from "./bootContract.ts";

type RequiredAction = BootstrapPayload["required_action"];

// Where the user goes to clear each pending action.
const targets: Record<NonNullable<RequiredAction>, string> = {
  reset_password: "/force_reset_password",
  select_office: "/select/office",
  privacy_notice: "/privacy-notice-agreement",
};

// Bounce someone off /login and they can never log in.
const authFlowPages = [
  "/login",
  "/logout",
  "/forgotpassword",
  "/resetpassword",
  "/unblock",
  "/auth",
];

const noReturnTo = [
  "/resetpassword/",
  "/login",
  "/session",
  "/agree_to_terms",
  "/select/office",
  "/api/v",
  "/pusher/auth",
  "/auth/token",
];

function matchesPage(pages: string[], pathname: string): boolean {
  return pages.some(
    (page) => pathname === page || pathname.startsWith(`${page}/`),
  );
}

function isAuthFlowPage(pathname: string): boolean {
  return matchesPage(authFlowPages, pathname);
}

// A gate firing here would bounce the user off the page that clears it.
function isRequiredActionPage(pathname: string): boolean {
  return matchesPage(Object.values(targets), pathname);
}

type BootLocation = Pick<Location, "pathname" | "search" | "hash">;

export function bootRedirectTarget(
  bootstrap: Pick<
    BootstrapPayload,
    "logged_in" | "required_action" | "otp_pending"
  >,
  { pathname, search, hash }: BootLocation,
): string | null {
  // The server keeps this in the session; a client redirect hands it over.
  const returnTo = encodeURIComponent(pathname + search + hash);

  if (!bootstrap.logged_in) {
    return isAuthFlowPage(pathname) ? null : `/login?return_to=${returnTo}`;
  }

  // Broader than the server, which does bounce such a user off /login. Must
  // narrow when /login moves to a shell, or they get a login page, not a gate.
  if (
    bootstrap.required_action &&
    !isAuthFlowPage(pathname) &&
    !isRequiredActionPage(pathname)
  ) {
    return targets[bootstrap.required_action];
  }

  // After the required-action check: the order the server applies them in.
  if (bootstrap.otp_pending && !isAuthFlowPage(pathname)) {
    return `/login/otp?return_to=${returnTo}`;
  }

  return null;
}

const RETURN_TO_KEY = "cvp-return-to";

// These two end on /home regardless, so a stash for them would only linger.
const endsElsewhere: ReadonlySet<string> = new Set([
  targets.reset_password,
  targets.select_office,
]);

// replace, like the server's 302: back must not land on a page that redirects
// here again. A parameter below rather than an import, because this file stays
// free of runtime imports and jsdom makes location.replace read-only.
function replaceLocation(path: string) {
  window.location.replace(path);
}

// A server-rendered page gates before any HTML reaches the browser, so this
// only ever fires on statically-served shells.
export function enforceBootRedirect(
  bootstrap: BootstrapPayload,
  replace = replaceLocation,
): boolean {
  const target = bootRedirectTarget(bootstrap, window.location);

  if (!target) {
    return false;
  }

  // The fragment, which the server never receives, is why this is held here.
  if (!endsElsewhere.has(target)) {
    const { pathname, search, hash } = window.location;
    const here = pathname + search + hash;

    // The second gate of a chain - login, then OTP - sees only where the
    // server dropped the user, which is less than the first gate recorded.
    if (isValidReturnTo(here) && !storedReturnTo()) {
      try {
        sessionStorage.setItem(RETURN_TO_KEY, here);
      } catch {
        // Without storage the user lands wherever the server sends them.
      }
    }
  }

  replace(target);

  return true;
}

// Anyone can edit sessionStorage, so a stored destination is untrusted input.
// Stricter than the server, which has to allow /login/[a-z] for its own flows.
function isValidReturnTo(value: string): boolean {
  // A browser reads a backslash as a slash, so /\evil.example leaves the site.
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\")
  ) {
    return false;
  }

  return noReturnTo.every((rejected) => !value.includes(rejected));
}

function storedReturnTo(): string | null {
  let value: string | null;

  try {
    value = sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }

  return value && isValidReturnTo(value) ? value : null;
}

export function consumeReturnTo(): string | null {
  const value = storedReturnTo();

  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Blocked storage, so there was nothing to read either.
  }

  return value;
}

// The last leg of the round trip the gates above start.
export function restoreReturnTo(
  bootstrap: Pick<BootstrapPayload, "required_action">,
  replace = replaceLocation,
): boolean {
  const { pathname, search, hash } = window.location;

  // Before consuming: on the page that clears a gate, restoring would bounce
  // the user off it and straight back into the gate.
  if (
    bootstrap.required_action ||
    isAuthFlowPage(pathname) ||
    isRequiredActionPage(pathname)
  ) {
    return false;
  }

  const target = consumeReturnTo();

  if (!target || target === pathname + search + hash) {
    return false;
  }

  replace(target);

  return true;
}

// Must stay in sync with the backend's definition of an internal user.
const internalRoles: ReadonlySet<Role> = new Set([
  "limited_access",
  "consultant",
  "countrymanager",
  "referencemanager",
  "internationalmanager",
]);

export function internalUserRedirectTarget(
  bootstrap: Pick<BootstrapPayload, "current_user_roles">,
): string | null {
  const roles = bootstrap.current_user_roles;

  // No user to judge; bootRedirectTarget has already sent them to /login.
  if (!roles) {
    return null;
  }

  return roles.some((role) => internalRoles.has(role)) ? null : "/home";
}

export function enforceInternalUser(
  bootstrap: BootstrapPayload,
  replace = replaceLocation,
): boolean {
  const target = internalUserRedirectTarget(bootstrap);

  if (!target) {
    return false;
  }

  replace(target);

  return true;
}
